/**
 * VendorInvoices
 *
 * Purpose:
 * - Unified invoices page for supplier and contractor invoices in vendor context.
 * - Manages tabs, filters, and actions (receive invoice, view invoice) with returnTab.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import CustomTable from '../../components/table/table';
import { VENDOR, VENDOR_INVOICES } from "../../constants/route-paths";
import { getvendorsPOList, getPurchaseOrder, downloadProjectPO, toggleDeleteVendorInvoice } from '../../actions/project-management.action';
import { format } from "date-fns";
import { numberFormat } from "../../util/currency-formatter";
import Popover from 'react-bootstrap/Popover';
import DownloadDialog from '../../components/download-dialog';
import DeleteDialog from '../../components/delete-dialog';
import PayInvoice from '../vendor-payment/pay-invoice';
import ReceiveCredit from '../suppliers/vendor-payment/receive-credit';
import { getGlobalSettingTemplateTypes } from '../../actions/global-setting-template.action';
import { warningSnackBar, successSnackBar } from '../../actions/common.action';
import { savePurchaseOrder, getProjectList, getPoVendors, getPOList } from '../../actions/project-management.action';
import { getGlobalSetting } from '../../actions/global-setting.action';
import { getVendorList } from '../../actions/vendor-management.action';
import DateFilter from '../../components/date-filter-dialog';
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Autorenew } from "@mui/icons-material";
import { Button } from "@mui/material";

import { Tooltip } from "@mui/material";
import './vendor.scss';
import idx from "idx";
import { fetchAccountingConnections } from "../../slice/accounting.slice";
import { VENDOR_TYPE, VENDOR_TYPE_LABEL, VENDOR_TAB, VENDOR_URL_PARAM, INVOICE_STATUS_TAB } from "../../constants/vendor.contant";
import SyncInvoicesModal from "../../components/sync-invoices-modal";

function VendorInvoices({ rSupplierPermission, wSupplierPermission, rContractorPermission, wContractorPermission, writePermission, wFinancePermission, projectModule, ...props }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userData);
  const accountId = idx(user, _ => _.selected_account.id);
  const entitlements = useSelector((state) => state.user.entitlements) || {};
  const isAccountingEnabled = Boolean(entitlements.accountingIntegration && entitlements.accountingPush);
  const connections = useSelector((state) => state.accounting?.connections) || [];
  const selectedConnectionId = useSelector((state) => state.accounting?.selectedConnectionId);
  const showStatusTabs = isAccountingEnabled && connections.length > 0;
  const activeConnection = connections.find(c => c.id === selectedConnectionId) || connections[0];

  const [showSyncModal, setShowSyncModal] = useState(false);
  const hasFinanceWrite = wFinancePermission;

  useEffect(() => {
    if (accountId) {
      dispatch(fetchAccountingConnections());
    }
  }, [accountId, dispatch]);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get URL parameters for tab state
  const urlTab = searchParams.get(VENDOR_URL_PARAM.TAB);
  const urlSubTab = searchParams.get(VENDOR_URL_PARAM.SUB_TAB);
  const urlPoId = searchParams.get(VENDOR_URL_PARAM.PO_ID);

  // Get tab state from URL or default
  const getTabState = () => {
    let activeTab = urlTab;
    let activeSubTab = urlSubTab || INVOICE_STATUS_TAB.ACTIVE;

    // Validate and set defaults
    if (!activeTab || (activeTab !== VENDOR_TAB.SUPPLIER_INVOICES && activeTab !== VENDOR_TAB.CONTRACTOR_INVOICES)) {
      activeTab = rSupplierPermission ? VENDOR_TAB.SUPPLIER_INVOICES : VENDOR_TAB.CONTRACTOR_INVOICES;
    }

    // Check if user has permission for the requested tab
    if (activeTab === VENDOR_TAB.SUPPLIER_INVOICES && !rSupplierPermission) activeTab = VENDOR_TAB.CONTRACTOR_INVOICES;
    else if (activeTab === VENDOR_TAB.CONTRACTOR_INVOICES && !rContractorPermission) activeTab = VENDOR_TAB.SUPPLIER_INVOICES;

    if (activeSubTab !== INVOICE_STATUS_TAB.ACTIVE && activeSubTab !== INVOICE_STATUS_TAB.POSTED) {
      activeSubTab = INVOICE_STATUS_TAB.ACTIVE;
    }

    return { activeTab, activeSubTab };
  };

  const [state, setState] = useState({
    supplierInvoices: [], // Original data
    contractorInvoices: [], // Original data
    supplierInvoicesTable: [], // Filtered data for display
    contractorInvoicesTable: [], // Filtered data for display
    loading: false,
    selectedInvoice: null,
    showPayInvoice: false,
    showReceiveCredit: false,
    invoiceData: {},
    templateOptions: [],
    downloadData: {
      open: false,
      id: "",
      template: "",
    },
    projectData: null,
    showFilter: false,
    startDate: null,
    endDate: null,
    invoiceType: 'ALL',
    deleteDialog: false,
    deleteInvoiceId: '',
    deleteInvoiceData: null,
    syncStatusCounts: { active: 0, posted: 0 },
    ...getTabState()
  });

  const [poSearchValue, setPoSearchValue] = useState("");

  // Update URL with tab state
  const updateURL = (tab, subTab) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(VENDOR_URL_PARAM.TAB, tab);
    if (showStatusTabs && subTab) {
      newSearchParams.set(VENDOR_URL_PARAM.SUB_TAB, subTab);
    } else {
      newSearchParams.delete(VENDOR_URL_PARAM.SUB_TAB);
    }
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  // Handle tab change
  const handleTabChange = (key) => {
    setState(prev => ({
      ...prev,
      activeTab: key,
      activeSubTab: INVOICE_STATUS_TAB.ACTIVE,
      // Clear filter values when switching tabs
      startDate: null,
      endDate: null,
      invoiceType: 'ALL',
      // Reset table data to original data
      supplierInvoicesTable: prev.supplierInvoices,
      contractorInvoicesTable: prev.contractorInvoices
    }));
    updateURL(key, INVOICE_STATUS_TAB.ACTIVE);
  };

  const handleSubTabChange = (key) => {
    setState(prev => ({
      ...prev,
      activeSubTab: key,
      startDate: null,
      endDate: null,
      invoiceType: 'ALL'
    }));
    updateURL(state.activeTab, key);
  };

  useEffect(() => {
    // Redirect to default tab if no search params found
    if (!urlTab) {
      const defaultTab = rSupplierPermission ? VENDOR_TAB.SUPPLIER_INVOICES : VENDOR_TAB.CONTRACTOR_INVOICES;
      updateURL(defaultTab, showStatusTabs ? state.activeSubTab : undefined);
      return;
    }

    if (showStatusTabs && urlSubTab && urlSubTab !== state.activeSubTab) {
      setState(prev => ({ ...prev, activeSubTab: urlSubTab }));
    }

    // Load data based on active tab
    if (state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES && rSupplierPermission) {
      loadSupplierInvoices(state.activeSubTab);
    } else if (state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES && rContractorPermission) {
      loadContractorInvoices(state.activeSubTab);
    }
  }, [state.activeTab, state.activeSubTab, urlTab, urlSubTab, urlPoId, location.pathname, navigate, rSupplierPermission, rContractorPermission, showStatusTabs, activeConnection?.id]);

  // Resolve the URL poId into the human-readable PO number and pre-populate
  // the table search bar. CustomTable filters the loaded list client-side.
  useEffect(() => {
    const resolvePoNumber = async () => {
      if (!urlPoId) {
        setPoSearchValue("");
        return;
      }

      try {
        const query = `?poId=${urlPoId}&woId=${urlPoId}`;
        const res = await dispatch(getPurchaseOrder(query));
        const poNumber = res?.f_id || res?.id || "";
        if (poNumber) {
          setPoSearchValue(poNumber.toString());
        }
      } catch (error) {
        console.error('Error resolving PO number for search:', error);
      }
    };

    resolvePoNumber();
  }, [urlPoId, dispatch]);

  const loadSupplierInvoices = async (subTab = state.activeSubTab) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const params = { vendorType: 'Supplier' };
      if (urlPoId) {
        params.poId = urlPoId;
      }
      if (showStatusTabs) {
        params.syncStatus = subTab;
        if (activeConnection?.id) {
          params.connectionId = activeConnection.id;
        }
      }
      const res = await dispatch(getvendorsPOList(params));
      const list = res?.list || [];
      const syncStatusCounts = res?.syncStatusCounts || { active: 0, posted: 0 };

      // Auto-switch to Posted if coming with poId (without explicit subTab) and only posted invoices exist
      if (urlPoId && !urlSubTab && showStatusTabs && subTab === INVOICE_STATUS_TAB.ACTIVE && syncStatusCounts.active === 0 && syncStatusCounts.posted > 0) {
        setState(prev => ({ ...prev, activeSubTab: INVOICE_STATUS_TAB.POSTED }));
        updateURL(state.activeTab, INVOICE_STATUS_TAB.POSTED);
        return loadSupplierInvoices(INVOICE_STATUS_TAB.POSTED);
      }

      // Filter out credit invoices without invoice IDs
      let filteredList = (Array.isArray(list) && list.filter(({ isCreditInvoice, invoiceId }) => !(isCreditInvoice && !invoiceId))) || [];
      if (urlPoId) {
        filteredList = filteredList.filter(item => String(item.poId) === String(urlPoId));
      }
      const suppliers = (Array.isArray(filteredList) && filteredList.filter(({ vendorType }) => vendorType === "Supplier")) || [];
      const formattedData = formatSupplierInvoiceData(suppliers);
      setState(prev => ({
        ...prev,
        supplierInvoices: formattedData, // Original data
        supplierInvoicesTable: formattedData, // Display data (initially same as original)
        syncStatusCounts: showStatusTabs ? syncStatusCounts : prev.syncStatusCounts,
        loading: false
      }));
    } catch (error) {
      console.error('Error loading supplier invoices:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const loadContractorInvoices = async (subTab = state.activeSubTab) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const params = { vendorType: VENDOR_TYPE.SUB_CONTRACTOR };
      if (urlPoId) {
        params.poId = urlPoId;
      }
      if (showStatusTabs) {
        params.syncStatus = subTab;
        if (activeConnection?.id) {
          params.connectionId = activeConnection.id;
        }
      }
      const res = await dispatch(getvendorsPOList(params));
      const list = res?.list || [];
      const syncStatusCounts = res?.syncStatusCounts || { active: 0, posted: 0 };

      // Auto-switch to Posted if coming with poId (without explicit subTab) and only posted invoices exist
      if (urlPoId && !urlSubTab && showStatusTabs && subTab === INVOICE_STATUS_TAB.ACTIVE && syncStatusCounts.active === 0 && syncStatusCounts.posted > 0) {
        setState(prev => ({ ...prev, activeSubTab: INVOICE_STATUS_TAB.POSTED }));
        updateURL(state.activeTab, INVOICE_STATUS_TAB.POSTED);
        return loadContractorInvoices(INVOICE_STATUS_TAB.POSTED);
      }

      // Filter out credit invoices without invoice IDs
      let filteredList = (Array.isArray(list) && list.filter(({ isCreditInvoice, invoiceId }) => !(isCreditInvoice && !invoiceId))) || [];
      if (urlPoId) {
        filteredList = filteredList.filter(item => String(item.poId) === String(urlPoId));
      }
      const contractors = (Array.isArray(filteredList) && filteredList.filter(({ vendorType }) => vendorType === VENDOR_TYPE.SUB_CONTRACTOR)) || [];
      const formattedData = formatContractorInvoiceData(contractors);
      setState(prev => ({
        ...prev,
        contractorInvoices: formattedData, // Original data
        contractorInvoicesTable: formattedData, // Display data (initially same as original)
        syncStatusCounts: showStatusTabs ? syncStatusCounts : prev.syncStatusCounts,
        loading: false
      }));
    } catch (error) {
      console.error('Error loading contractor invoices:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const formatSupplierInvoiceData = (invoices) => {
    return invoices.map((invoice, index) => ({
      vendorName: invoice.vendorName || '',
      invoiceType: invoice.isCreditInvoice ? "Credit" : "Regular",
      invoiceNo: invoice.invoiceNo,
      poNumber: invoice.poNumber,
      fId: invoice.fId || invoice.poNumber, // For project prefix and suffix
      projectName: invoice.projectName || '',
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      amountPaid: invoice.amountPaid || 0,
      userName: invoice.userName || '',
      invoiceId: invoice.invoiceId,
      vendorType: "Supplier",
      projectId: invoice.projectId,
      vendorId: invoice.vendorId,
      poId: invoice.poId,
      isCreditInvoice: invoice.isCreditInvoice || false,
    }));
  };

  const formatContractorInvoiceData = (invoices) => {
    return invoices.map((invoice, index) => ({
      vendorName: invoice.vendorName || '',
      invoiceType: invoice.isCreditInvoice ? "Credit" : "Regular",
      invoiceNo: invoice.invoiceNo,
      poNumber: invoice.poNumber, // For contractors, this is WO number
      fId: invoice.fId || invoice.poNumber, // For project prefix and suffix
      projectName: invoice.projectName || '',
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      amountPaid: invoice.amountPaid || 0,
      userName: invoice.userName || '',
      invoiceId: invoice.invoiceId,
      vendorType: VENDOR_TYPE.SUB_CONTRACTOR,
      projectId: invoice.projectId,
      vendorId: invoice.vendorId,
      poId: invoice.poId,
      isCreditInvoice: invoice.isCreditInvoice || false,
      progressClaimNumber: invoice.progressClaimNumber,
    }));
  };

  // Supplier Invoice columns - reusing existing supplier invoice columns
  const supplierInvoiceColumns = [
    {
      title: "Vendor Name",
      field: "vendorName",
      dataCy: "vendor-name",
      render: (rowData) => rowData.vendorName
    },
    {
      title: "Invoice Type",
      field: "invoiceType",
      dataCy: "invoice-type",
      render: (rowData) => rowData.invoiceType
    },
    {
      title: "Invoice No",
      field: "invoiceNo",
      dataCy: "invoice-no",
      render: (rowData) => (
        <span>{rowData.invoiceNo}</span>
      ),
    },
    {
      title: "PO Number",
      field: "poNumber",
      dataCy: "po-number",
      /* fId is the PO number with project prefix and Suffix if null then default to po Number*/
      render: (rowData) => rowData.fId || rowData.poNumber
    },
    { title: "Project/Job", field: "projectName", render: (rowData) => rowData.projectName },
    {
      title: "Invoice Date",
      field: "invoiceDate",
      type: 'date',
      dataCy: "invoice-date",
      render: (rowData) => (rowData.invoiceDate && format(new Date(rowData.invoiceDate), "dd/MM/yyyy")) || "",
    },
    {
      title: "Due Date",
      field: "dueDate",
      type: 'date',
      dataCy: "due-date",
      render: (rowData) => rowData.dueDate ? format(new Date(rowData.dueDate), "dd/MM/yyyy") : "",
    },
    {
      title: "Amount Paid / Credit Amount",
      field: "amountPaid",
      dataCy: "amount-paid",
      render: (rowData) => `${numberFormat(rowData.amountPaid)}`,
      class: 'text-right',
    },
    {
      title: "Received By",
      field: "userName",
      dataCy: "received-by",
      render: (rowData) => rowData.userName
    },
  ];

  // Contractor Invoice columns - reusing existing contractor invoice columns
  const contractorInvoiceColumns = [
    { title: "Vendor Name", field: "vendorName", render: (rowData) => rowData.vendorName, dataCy: "vendor-name" },
    {
      title: "Invoice No",
      field: "invoiceNo",
      dataCy: "invoice-no",
      render: (rowData) => (
        <span>{rowData.invoiceNo}</span>
      ),
    },
    {
      title: "WO Number",
      field: "poNumber",
      dataCy: "po-number",
      /* fId is the WO number with project prefix and Suffix if null then default to wo Number*/
      render: (rowData) => rowData.fId || rowData.poNumber
    },
    { title: "Project/Job", field: "projectName", render: (rowData) => rowData.projectName, dataCy: "project-job" },
    {
      title: "Invoice Date",
      field: "invoiceDate",
      dataCy: "invoice-date",
      render: (rowData) => (rowData.invoiceDate && format(new Date(rowData.invoiceDate), "dd/MM/yyyy")) || "",
    },
    {
      title: "Due Date",
      field: "dueDate",
      dataCy: "due-date",
      render: (rowData) => rowData.dueDate ? format(new Date(rowData.dueDate), "dd/MM/yyyy") : "",
    },
    {
      title: "Amount Paid",
      field: "amountPaid",
      dataCy: "amount-paid",
      render: (rowData) => `${numberFormat(rowData.amountPaid)}`,
      class: 'text-right',
    },
    {
      title: "Progress Claim",
      field: "progressClaimNumber",
      dataCy: "progress-claim",
      render: (rowData) => rowData.progressClaimNumber || "-",
    },
    {
      title: "Received By",
      field: "userName",
      dataCy: "received-by",
      render: (rowData) => rowData.userName
    },
  ];

  // Invoice action handlers - reusing existing logic
  const handleMenu = (rowData) => {
    setState(prev => ({
      ...prev,
      selectedInvoice: rowData,
      invoiceData: {
        projectId: rowData.projectId,
        vendorId: rowData.vendorId,
        poId: rowData.poId,
        poNo: rowData.poNumber,
        isSubContractor: rowData.vendorType === VENDOR_TYPE.SUB_CONTRACTOR,
        isCreditInvoice: rowData.isCreditInvoice,
      },
    }));
  };

  const handlePayInvoiceModal = (status, allProjects = false) => {
    if (!status) {
      // Reload data based on active tab
      if (state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES) {
        loadSupplierInvoices();
      } else if (state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES) {
        loadContractorInvoices();
      }
    }
    setState(prev => ({
      ...prev,
      showPayInvoice: status,
      invoiceData: { ...(allProjects ? {} : { ...prev.invoiceData }) },
    }))
  };

  const handleReceiveCreditModal = (status, allProjects = false) => {
    if (status !== false) {
      // Reload data based on active tab
      if (state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES) {
        loadSupplierInvoices();
      } else if (state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES) {
        loadContractorInvoices();
      }
    }
    setState(prev => ({
      ...prev,
      showReceiveCredit: status,
      invoiceData: { ...(allProjects ? {} : { ...prev.invoiceData }) },
    }));
  };

  const viewPO = () => {
    const { selectedInvoice } = state;
    if (selectedInvoice) {
      const invoiceType = state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES ? VENDOR_TYPE.SUPPLIER : VENDOR_TYPE.CONTRACTOR;
      const subTabQuery = showStatusTabs ? `&${VENDOR_URL_PARAM.RETURN_SUB_TAB}=${state.activeSubTab}` : '';
      navigate(`/${VENDOR}/${VENDOR_INVOICES}/${invoiceType}/view/${selectedInvoice.invoiceId}?${VENDOR_URL_PARAM.RETURN_TAB}=${state.activeTab}${subTabQuery}${selectedInvoice.projectId ? `&id=${selectedInvoice.projectId}` : ''}${selectedInvoice.poId ? `&poId=${selectedInvoice.poId}` : ''}`);
    }
  };

  const handleDownloadDialog = (id = "", templateType = "", overrideData = null) => {
    const resolvedTemplateType = templateType || (state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES ? 'purchase_order' : 'subcontractor_payment_schedule');
    const currentId = id && (typeof id === 'string' || typeof id === 'number') ? id : (state.selectedInvoice?.invoiceId || state.invoiceId);
    const { downloadData } = state;
    const isOpening = !downloadData.open;

    const newDownloadData = {
      open: isOpening,
      id: currentId || "",
      template: "",
      overrideData: overrideData
    };

    if (isOpening) {
      gettemplate(resolvedTemplateType);
    }

    setState(prev => ({
      ...prev,
      downloadData: newDownloadData
    }))
  };

  const gettemplate = async (type) => {
    let newdata = [];
    const templateData = await dispatch(getGlobalSettingTemplateTypes(type, null));
    if (Array.isArray(templateData)) {
      templateData.forEach((item) => {
        newdata.push({
          value: item.id,
          key: item.name,
        });
      });
    }
    setState(prev => ({
      ...prev,
      templateOptions: newdata
    }))
  };

  const downloadFile = async (fileFormat = "pdf", tempId) => {
    try {
      const { downloadData } = state;
      if (fileFormat === "pdf" || fileFormat === "ALL") {
        await dispatch(downloadProjectPO(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData))
          .catch(() => dispatch(warningSnackBar('Failed to download PDF Payment Schedule')));
      }
      if (fileFormat === "docx" || fileFormat === "ALL") {
        await dispatch(downloadProjectPO(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData))
          .catch(() => dispatch(warningSnackBar('Failed to download DOCX Payment Schedule')));
      }
      handleDownloadDialog();
    } catch (error) {
      dispatch(warningSnackBar('An error occurred while downloading the Payment Schedule'));
    }
  };

  const handleDeleteInvoiceDialog = (invoiceId = '', invoiceData = null) => {
    setState(prev => ({
      ...prev,
      deleteDialog: !prev.deleteDialog,
      deleteInvoiceId: invoiceId,
      deleteInvoiceData: invoiceData
    }));
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await dispatch(toggleDeleteVendorInvoice(invoiceId));
      handleDeleteInvoiceDialog();

      // Refresh data based on active tab
      if (state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES) {
        loadSupplierInvoices();
      } else {
        loadContractorInvoices();
      }
    } catch (error) {
      dispatch(warningSnackBar('Error deleting invoice'));
      handleDeleteInvoiceDialog();
    }
  };

  const renderInvoiceTable = (activeData, invoiceType) => (
    <CustomTable
      columns={invoiceType === VENDOR_TAB.SUPPLIER_INVOICES ? supplierInvoiceColumns : contractorInvoiceColumns}
      data={activeData}
      externalSearchValue={poSearchValue}
      isAction={true}
      handleMenu={(data) => handleMenu(data)}
      dataCy={(invoiceType === VENDOR_TAB.SUPPLIER_INVOICES ? "supplier" : "contractor") + "-invoice-list-table"}
      menu={(data) => (
        <Popover id="popover-basic" data-cy="invoice-list-table-menu">
          <Popover.Body>
            <ul className="action-block-list">
              <li onClick={() => viewPO()} data-cy="view-invoice">
                View Invoice
              </li>
              {data.vendorType === VENDOR_TYPE.SUB_CONTRACTOR && (
                <li onClick={() => handleDownloadDialog(data.invoiceId)} data-cy="download-payment-schedule">
                  Download Payment Schedule
                </li>
              )}
              <li className="text-danger" onClick={() => handleDeleteInvoiceDialog(data.invoiceId, data)} data-cy="deleteInvoice">
                Delete
              </li>
            </ul>
          </Popover.Body>
        </Popover>
      )}
      fileName={`${invoiceType === VENDOR_TAB.SUPPLIER_INVOICES ? "Supplier Invoices" : "Contractor Invoices"} List`}
      loading={state.loading}
    />
  );

  // Filter functions
  const convertDateFormat = (dateString) => {
    if (!dateString) return null;
    let date = Date.parse(dateString);
    if (isNaN(date)) return null;
    date = new Date(date);
    return date;
  };

  const showFilterHandler = () => {
    setState(prev => ({
      ...prev,
      showFilter: !prev.showFilter,
    }));
  };

  const resetInvoiceData = () => {
    // Reload data based on active tab
    if (state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES) {
      loadSupplierInvoices();
    } else if (state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES) {
      loadContractorInvoices();
    }
  };

  const dateChangeFilter = (startDate, endDate, invoiceType) => {
    // Filter supplier invoices
    if (state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES) {
      let table = [...state.supplierInvoices];

      if (startDate || endDate) {
        table = table.filter(({ invoiceDate }) => {
          const newDate = convertDateFormat(invoiceDate);
          if (!newDate || isNaN(newDate.getTime())) return false;

          const startDateObj = startDate ? new Date(startDate) : null;
          const endDateObj = endDate ? new Date(endDate) : null;

          let matchesStart = true;
          let matchesEnd = true;

          if (startDateObj && !isNaN(startDateObj.getTime())) {
            // Set time to start of day for start date
            startDateObj.setHours(0, 0, 0, 0);
            matchesStart = newDate >= startDateObj;
          }

          if (endDateObj && !isNaN(endDateObj.getTime())) {
            // Set time to end of day for end date
            endDateObj.setHours(23, 59, 59, 999);
            matchesEnd = newDate <= endDateObj;
          }

          return matchesStart && matchesEnd;
        });
      }

      if (invoiceType && invoiceType !== 'ALL') {
        table = table.filter((item) => {
          const itemInvoiceType = item.isCreditInvoice ? 'Credit' : 'Regular';
          return itemInvoiceType === invoiceType;
        });
      }

      setState(prev => ({
        ...prev,
        supplierInvoicesTable: table,
        startDate,
        endDate,
        invoiceType: invoiceType || 'ALL',
        showFilter: false
      }));
    }
    // Filter contractor invoices
    else if (state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES) {
      let table = [...state.contractorInvoices];

      if (startDate || endDate) {
        table = table.filter(({ invoiceDate }) => {
          const newDate = convertDateFormat(invoiceDate);
          if (!newDate || isNaN(newDate.getTime())) return false;

          const startDateObj = startDate ? new Date(startDate) : null;
          const endDateObj = endDate ? new Date(endDate) : null;

          let matchesStart = true;
          let matchesEnd = true;

          if (startDateObj && !isNaN(startDateObj.getTime())) {
            // Set time to start of day for start date
            startDateObj.setHours(0, 0, 0, 0);
            matchesStart = newDate >= startDateObj;
          }

          if (endDateObj && !isNaN(endDateObj.getTime())) {
            // Set time to end of day for end date
            endDateObj.setHours(23, 59, 59, 999);
            matchesEnd = newDate <= endDateObj;
          }

          return matchesStart && matchesEnd;
        });
      }

      if (invoiceType && invoiceType !== 'ALL') {
        table = table.filter((item) => {
          const itemInvoiceType = item.isCreditInvoice ? 'Credit' : 'Regular';
          return itemInvoiceType === invoiceType;
        });
      }

      setState(prev => ({
        ...prev,
        contractorInvoicesTable: table,
        startDate,
        endDate,
        invoiceType: invoiceType || 'ALL',
        showFilter: false
      }));
    }
  };

  const _getPOList = (params) => {
    return dispatch(getPOList(params)).then(({ list }) => {
      return list;
    });
  }

  return (
    <>
      {/* Modal Components */}
      <DownloadDialog
        {...(state.downloadData || {})}
        downloadFile={downloadFile}
        templateOptions={state.templateOptions}
        handleDownloadDialog={handleDownloadDialog}
        downloadModuleName="Payment Schedule"
        dataCy="download-payment-schedule"
      />
      <SyncInvoicesModal
        show={showSyncModal}
        onHide={() => setShowSyncModal(false)}
        entityType="bill"
        projectId={null}
        onSyncSuccess={resetInvoiceData}
        fixedBillTab={state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES ? VENDOR_TYPE.SUPPLIER : VENDOR_TYPE.CONTRACTOR}
      />
      <DeleteDialog
        open={state.deleteDialog}
        dialogClose={() => handleDeleteInvoiceDialog()}
        deleteHandler={handleDeleteInvoice}
        id={state.deleteInvoiceId}
        moduleName="Invoice"
        description="this invoice"
        contentClassName="text-center"
        fullWidth={false}
        maxWidth="xs"
        dataCy="delete-invoice-dialog"
      >
        <div className="text-center">
          <p>Are you sure you want to delete this invoice?</p>
          {state.deleteInvoiceData && (
            <div className="bg-light border rounded p-3 mb-3 text-start">
              <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
                <span>Invoice No</span>
                <strong>{state.deleteInvoiceData.invoiceNo || '-'}</strong>
              </div>
              <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
                <span>Vendor</span>
                <strong>{state.deleteInvoiceData.vendorName || '-'}</strong>
              </div>
              <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
                <span>{state.deleteInvoiceData.vendorType === VENDOR_TYPE.SUB_CONTRACTOR ? 'WO Number' : 'PO Number'}</span>
                <strong>{state.deleteInvoiceData.fId || state.deleteInvoiceData.poNumber || '-'}</strong>
              </div>
              <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
                <span>Project/Job</span>
                <strong>{state.deleteInvoiceData.projectName || '-'}</strong>
              </div>
              <div className="d-flex justify-content-between gap-3 py-1">
                <span>Amount</span>
                <strong>{numberFormat(state.deleteInvoiceData.amountPaid || 0)}</strong>
              </div>
            </div>
          )}
          <p className="text-danger fw-bold mb-0">This action cannot be undone.</p>
        </div>
      </DeleteDialog>
      {state.showPayInvoice && (
        <PayInvoice
          {...(state.invoiceData || {})}
          handleClose={() => handlePayInvoiceModal(false)}
          handleDownloadDialog={handleDownloadDialog}
          getInvoiceList={state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES ? loadSupplierInvoices : loadContractorInvoices}
          isSubContractor={state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES}
          workType="project"
          initialVendorType={state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES ? VENDOR_TYPE_LABEL.SUPPLIER : VENDOR_TYPE_LABEL.CONTRACTOR}
          dataCy="receive-invoice-modal"
        />
      )}
      {state.showReceiveCredit && (
        <ReceiveCredit
          {...(state.invoiceData || {})}
          workType="project"
          handleDownloadDialog={handleDownloadDialog}
          handleClose={handleReceiveCreditModal}
          savePurchaseOrder={(data, type, message) => dispatch(savePurchaseOrder(data, type, message))}
          successSnackBar={(message) => dispatch(successSnackBar(message))}
          warningSnackBar={(message) => dispatch(warningSnackBar(message))}
          getProjectList={(projectId, type) => dispatch(getProjectList(projectId, type))}
          getPoVendors={(projectId) => dispatch(getPoVendors(projectId))}
          getPOList={_getPOList}
          getGlobalSetting={(type) => dispatch(getGlobalSetting(type))}
          getVendorList={(type, status) => dispatch(getVendorList(type, status))}
          resetInvoiceData={resetInvoiceData}
          isSubContractor={state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES}
          initialVendorType={state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES ? 'Supplier' : VENDOR_TYPE.SUB_CONTRACTOR}
          currentActiveTab={state.activeTab}
        />
      )}
      {state.showFilter && (
        <DateFilter
          startDate={state.startDate}
          endDate={state.endDate}
          invoiceType={state.invoiceType}
          changeFilter={dateChangeFilter}
          dialogClose={showFilterHandler}
          open={state.showFilter}
          showInvoiceType={true}
          dataCy="date-filter-modal"
        />
      )}

      {projectModule === false ? (
        <p>
          You do not currently have access to any Projects. Please
          contact your Administrator if this is required.
        </p>
      ) : (
        <div className="purchase-order-block vendor-management" data-cy="vendor-invoices">
          <div className="row">
            <div className="col-12">
              <h2 className="content-heading text-uppercase" data-cy="invoices-heading">INVOICES</h2>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <div className="vendor-header">
                <Tabs
                  activeKey={state.activeTab}
                  id="vendor-tabs"
                  className="vendor-type-tabs mb-0"
                  onSelect={handleTabChange}
                >
                  {rSupplierPermission && (
                    <Tab eventKey={VENDOR_TAB.SUPPLIER_INVOICES} title={`SUPPLIER INVOICES`} tabClassName="supplier-invoices-tab" />
                  )}
                  {rContractorPermission && (
                    <Tab eventKey={VENDOR_TAB.CONTRACTOR_INVOICES} title={`CONTRACTOR INVOICES`} tabClassName="contractor-invoices-tab" />
                  )}
                </Tabs>

                <div className="w-auto ms-auto mt-auto">
                  {writePermission && (
                    <>
                      {state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES && (
                        <>
                          {isAccountingEnabled && connections.length > 0 && (
                            <Button
                              variant="outlined"
                              className="secondarybtn secondarybtn-outline btn-ch ms-2 me-2"
                              onClick={() => setShowSyncModal(true)}
                              disabled={!hasFinanceWrite}
                            >
                              SYNC BILLS
                            </Button>
                          )}
                          <button
                            className="receive-invoice-button secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                            data-cy="supplier-receive-credit"
                            onClick={() => handleReceiveCreditModal(true, true)}
                          >
                            RECEIVE CREDIT
                          </button>
                        </>
                      )}
                      {state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES && (
                        <>
                          {isAccountingEnabled && connections.length > 0 && (
                            <Button
                              variant="outlined"
                              className="secondarybtn secondarybtn-outline btn-ch ms-2 me-2"
                              onClick={() => setShowSyncModal(true)}
                              disabled={!hasFinanceWrite}
                            >
                              SYNC BILLS
                            </Button>
                          )}
                        </>
                      )}

                      <button
                        className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                        data-cy="receive-invoice-button"
                        onClick={() => handlePayInvoiceModal(true, true)}
                      >
                        RECEIVE INVOICE
                      </button>
                    </>
                  )}
                  <Tooltip title="Refresh Data">
                    <button
                      data-cy="refresh-button"
                      onClick={resetInvoiceData}
                      aria-label="refresh"
                      className="btn"
                    >
                      <Autorenew />
                    </button>
                  </Tooltip>
                  <Tooltip title="Filters">
                    <button
                      data-cy="filter-button"
                      onClick={showFilterHandler}
                      aria-label="filter"
                      className="btn"
                    >
                      <FilterAltIcon />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              {showStatusTabs && (
                <Tabs
                  activeKey={state.activeSubTab}
                  id="invoice-sub-tabs"
                  className="mb-3 sub-tabs"
                  onSelect={handleSubTabChange}
                >
                  <Tab eventKey={INVOICE_STATUS_TAB.ACTIVE} title={`Active (${state.syncStatusCounts?.active ?? 0})`} tabClassName="active-tab" />
                  <Tab eventKey={INVOICE_STATUS_TAB.POSTED} title={`Posted (${state.syncStatusCounts?.posted ?? 0})`} tabClassName="posted-tab" />
                </Tabs>
              )}
              {state.activeTab === VENDOR_TAB.SUPPLIER_INVOICES && rSupplierPermission && (
                renderInvoiceTable(state.supplierInvoicesTable, VENDOR_TAB.SUPPLIER_INVOICES)
              )}
              {state.activeTab === VENDOR_TAB.CONTRACTOR_INVOICES && rContractorPermission && (
                renderInvoiceTable(state.contractorInvoicesTable, VENDOR_TAB.CONTRACTOR_INVOICES)
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default VendorInvoices;
